/**
 * Pure Canvas-based renderer for branded share cards.
 * Draws a 1080×1080 card suitable for Instagram/social sharing.
 */

export interface ShareCardData {
  title: string;
  subtitle?: string;
  date?: string;
  countryFlag?: string;
  countryName?: string;
  accentColor?: string;
  /** When true, the bottom-right watermark is omitted (PTR+ users). */
  hideWatermark?: boolean;
}

const CARD_BG_START = '#0f172a';
const CARD_BG_END = '#1e293b';
const WATERMARK_OPACITY = 0.4;

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  lineHeight: number,
): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
}

export async function renderShareCard(
  ctx: CanvasRenderingContext2D,
  data: ShareCardData,
  width: number,
  height: number,
): Promise<void> {
  const accent = data.accentColor || '#6366f1';
  const padding = width * 0.08;
  const contentWidth = width - padding * 2;

  // Background gradient
  const grad = ctx.createLinearGradient(0, 0, width, height);
  grad.addColorStop(0, CARD_BG_START);
  grad.addColorStop(1, CARD_BG_END);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, width, height);

  // Accent stripe at top
  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, width, 6);

  // Accent glow (decorative circle)
  const glowGrad = ctx.createRadialGradient(
    width * 0.8, height * 0.2, 0,
    width * 0.8, height * 0.2, width * 0.4,
  );
  glowGrad.addColorStop(0, accent + '30');
  glowGrad.addColorStop(1, 'transparent');
  ctx.fillStyle = glowGrad;
  ctx.fillRect(0, 0, width, height);

  // Top: brand name
  ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
  ctx.font = `500 ${Math.round(width * 0.028)}px system-ui, -apple-system, sans-serif`;
  ctx.textAlign = 'left';
  ctx.fillText('vacant.day', padding, padding + width * 0.035);

  // Center section: title
  const titleSize = Math.round(width * 0.065);
  ctx.fillStyle = '#ffffff';
  ctx.font = `800 ${titleSize}px system-ui, -apple-system, sans-serif`;
  ctx.textAlign = 'left';
  const titleLines = wrapText(ctx, data.title, contentWidth, titleSize * 1.2);
  const totalTextHeight =
    titleLines.length * titleSize * 1.2 +
    (data.subtitle ? titleSize * 0.8 : 0) +
    (data.date ? titleSize * 0.8 : 0);
  let y = (height - totalTextHeight) / 2;

  for (const line of titleLines) {
    ctx.fillText(line, padding, y + titleSize);
    y += titleSize * 1.2;
  }

  // Subtitle
  if (data.subtitle) {
    y += titleSize * 0.2;
    const subSize = Math.round(width * 0.035);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.font = `400 ${subSize}px system-ui, -apple-system, sans-serif`;
    ctx.fillText(data.subtitle, padding, y + subSize);
    y += subSize * 1.4;
  }

  // Date
  if (data.date) {
    y += titleSize * 0.1;
    const dateSize = Math.round(width * 0.032);
    ctx.fillStyle = accent;
    ctx.font = `600 ${dateSize}px system-ui, -apple-system, sans-serif`;
    ctx.fillText(data.date, padding, y + dateSize);
  }

  // Bottom-left: country info
  if (data.countryName) {
    const bottomY = height - padding;
    const flagSize = Math.round(width * 0.035);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.font = `500 ${flagSize}px system-ui, -apple-system, sans-serif`;
    ctx.textAlign = 'left';
    const text = data.countryFlag ? `${data.countryFlag}  ${data.countryName}` : data.countryName;
    ctx.fillText(text, padding, bottomY);
  }

  // Bottom-right: watermark (hidden for PTR+ users)
  if (!data.hideWatermark) {
    const wmSize = Math.round(width * 0.03);
    ctx.fillStyle = `rgba(255, 255, 255, ${WATERMARK_OPACITY})`;
    ctx.font = `600 ${wmSize}px system-ui, -apple-system, sans-serif`;
    ctx.textAlign = 'right';
    ctx.fillText('vacant.day', width - padding, height - padding);
  }
}
